import styled from 'styled-components';

import { Drawer, useBreakpoint } from '@electricjs/arc';

import { useGlobalUI } from '@/components/GlobalUIProvider';

// Drawers are opened via a snapshot of props taken once at open time (see "openGlobalDrawer" in
// GlobalUIProvider), so anything that needs to stay correct across the drawer's whole lifetime
// (not just at the instant it opened) has to be recomputed here, where the Drawer actually
// renders, instead of baked into that snapshot:
// - width needs to react live to the viewport crossing a breakpoint while the drawer is already open
// - position needs to account for any active top banners (e.g. Product Support/partner-redirect), so the drawer
//   doesn't render over them and hide its own header underneath
//
// Note: this override (and the "width" override below) assumes the default placement ("right"), the only one any
// caller currently uses. A drawer opened with placement="top"/"bottom" would need its "bottom"/height offset
// overridden instead, which this doesn't handle. "placement" itself is also frozen at open time, same as every
// other drawer prop except the two overridden here.
const OffsetDrawer = styled(Drawer)<{ $bannerHeight: string }>`
  top: ${({ $bannerHeight }) => $bannerHeight};
  bottom: auto;
  height: calc(100% - ${({ $bannerHeight }) => $bannerHeight});
`;

/**
 * Renders all the dialogs (toast, modal, drawer) that are controlled by GlobalUIProvider.
 *
 * These dialogs components need to be descendants of all providers (e.g., StorefrontProvider, RouterProvider)
 * so they can interact with their contexts. And they should be rendered/instantiated only once in the tree.
 *
 * But as we have 2 subtrees of routes (with separately "RouterProvider"), we need to repeat them in each subtree.
 * So they were grouped inside "GlobalDialogs" component to be easily repeated in each subtree.
 */
export const GlobalDialogs = () => {
  const { modal, drawerContent, drawerProps, toast, bannerHeight } =
    useGlobalUI();

  const { sm } = useBreakpoint();

  return (
    <>
      {modal}
      <OffsetDrawer
        {...drawerProps}
        width={sm ? drawerProps.width : '100vw'}
        $bannerHeight={bannerHeight}>
        {drawerContent}
      </OffsetDrawer>
      {toast}
    </>
  );
};
